import React, { useState, useRef, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Button,
  Grid,
  Paper,
  IconButton,
  TextField,
  Collapse,
  Alert,
  Badge,
  Chip,
  CircularProgress
} from '@mui/material';
import {
  PhotoCamera,
  Upload,
  Delete,
  ExpandMore,
  ExpandLess,
  Visibility,
  Warning
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

// 🎯 COORDINATION SIMPLE: Variable globale pour gérer les conflits caméra
let globalActiveSection = null;

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

const PhotoPreview = styled(Paper)(({ theme }) => ({
  position: 'relative',
  aspectRatio: '1 / 1',
  overflow: 'hidden',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'scale(1.05)',
    boxShadow: theme.shadows[8],
  },
}));

const PhotoImage = styled('img')({
  width: '100%',
  height: '100%',
  objectFit: 'cover',
});

const PhotoActions = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 4,
  right: 4,
  display: 'flex',
  gap: 4,
  '& .MuiIconButton-root': {
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    color: 'white',
    padding: '4px',
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.9)',
    },
  },
}));

// 🎯 AMÉLIORATION: Animation flash pour la capture
const FlashOverlay = styled(Box)({
  '@keyframes flash': {
    '0%': { opacity: 0 },
    '50%': { opacity: 1 },
    '100%': { opacity: 0 }
  }
});

/**
 * 🔧 ROADMAP COMPONENT - Section de capture de photos
 * Interface webcam/upload multiple selon spécifications roadmap
 */
const PhotoCaptureSection = forwardRef(({
  photos = [],
  onPhotosChange,
  maxPhotos = 5,
  readOnly = false,
  photoType = 'equipment_state',
  optional = true,
  title, // Pas de valeur par défaut ici, sera géré dans le composant
  sectionId = 'default', // 🎯 ID unique pour identifier la section
  onCameraClose // 🎯 AMÉLIORATION: Callback pour fermer la caméra depuis l'extérieur
}, ref) => {
  const { t } = useTranslation('photoCapture');
  
  // Titre par défaut internationalisé
  const displayTitle = title || t('ui.defaultTitle', 'Photos Équipement (Optionnel)');
  
  const [expanded, setExpanded] = useState(true); // 🎯 AMÉLIORATION: Ouvrir l'accordéon par défaut
  const [cameraStream, setCameraStream] = useState(null);
  const [showCamera, setShowCamera] = useState(false);
  const [cameraLoading, setCameraLoading] = useState(false);
  const [videoReady, setVideoReady] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [errors, setErrors] = useState([]);
  const [capturedPhoto, setCapturedPhoto] = useState(null);
  const [photoCaptured, setPhotoCaptured] = useState(false);
  const [photoBlob, setPhotoBlob] = useState(null);
  const [isStartingCamera, setIsStartingCamera] = useState(false); // Protection contre clics multiples
  const [captureFlash, setCaptureFlash] = useState(false); // Effet flash lors de la capture
  const [availableCameras, setAvailableCameras] = useState([]);
  const [selectedCameraId, setSelectedCameraId] = useState('');
  const [isEnumeratingCameras, setIsEnumeratingCameras] = useState(false);
  
  // 🎯 COORDINATION: État de conflit avec autres sections
  const [isBlocked, setIsBlocked] = useState(false);
  
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);

  const pickPreferredCamera = useCallback((videoDevices) => {
    if (!videoDevices.length) {
      return null;
    }

    const backCamera = videoDevices.find((device) => {
      const label = (device.label || '').toLowerCase();
      return label.includes('back') || label.includes('rear') || label.includes('environment');
    });

    if (backCamera) {
      return backCamera;
    }

    if (videoDevices.length > 1) {
      return videoDevices[videoDevices.length - 1];
    }

    return videoDevices[0];
  }, []);

  const buildVideoConstraints = useCallback((cameraId = '') => {
    const baseConstraints = {
      width: { ideal: 1280 },
      height: { ideal: 720 }
    };

    if (cameraId) {
      return {
        deviceId: { exact: cameraId },
        ...baseConstraints
      };
    }

    return {
      facingMode: { ideal: 'environment' },
      ...baseConstraints
    };
  }, []);

  const enumerateCameras = useCallback(async () => {
    if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) {
      return [];
    }

    setIsEnumeratingCameras(true);
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoDevices = devices.filter((device) => device.kind === 'videoinput');
      setAvailableCameras(videoDevices);

      const preferredCamera = pickPreferredCamera(videoDevices);
      if (preferredCamera?.deviceId) {
        setSelectedCameraId((currentCameraId) => currentCameraId || preferredCamera.deviceId);
      }

      return videoDevices;
    } catch (error) {
      console.warn('📸 [PhotoCapture] Camera enumeration failed:', error);
      return [];
    } finally {
      setIsEnumeratingCameras(false);
    }
  }, [pickPreferredCamera]);

  // Formats autorisés selon constantes backend
  const allowedFormats = ['jpg', 'jpeg', 'png', 'webp'];
  const maxFileSize = 10 * 1024 * 1024; // 10MB

  useEffect(() => {
    return () => {
      // Nettoyer le stream camera
      if (cameraStream) {
        cameraStream.getTracks().forEach(track => track.stop());
      }
    };
  }, [cameraStream]);

  // 🎯 AMÉLIORATION: Fermer la caméra depuis l'extérieur
  useEffect(() => {
    if (onCameraClose && showCamera) {
      console.log('📸 [PhotoCapture] Fermeture caméra demandée depuis l\'extérieur');
      stopCamera();
    }
  }, [onCameraClose, showCamera]);

  // 🎯 COORDINATION: Libérer la caméra quand la section se ferme
  useEffect(() => {
    if (!expanded && globalActiveSection === sectionId) {
      console.log('📸 [PhotoCapture] Section fermée, libération automatique:', sectionId);
      globalActiveSection = null;
      stopCamera();
    }
  }, [expanded, sectionId]);

  // 🎯 COORDINATION: Surveiller les conflits avec autres sections
  useEffect(() => {
    const checkBlocked = () => {
      if (globalActiveSection && globalActiveSection !== sectionId) {
        if (!isBlocked) {
          console.log('📸 [PhotoCapture] Section bloquée par:', globalActiveSection);
          setIsBlocked(true);
          // Fermer la caméra si elle était ouverte
          if (showCamera) {
            stopCamera();
          }
        }
      } else if (isBlocked && (!globalActiveSection || globalActiveSection === sectionId)) {
        console.log('📸 [PhotoCapture] Section débloquée:', sectionId);
        setIsBlocked(false);
      }
    };
    
    // Vérifier immédiatement
    checkBlocked();
    
    // 🎯 CORRECTION: Vérification plus fréquente pour débloquer rapidement
    const interval = setInterval(checkBlocked, 200);
    
    return () => clearInterval(interval);
  }, [sectionId, isBlocked, showCamera]);

  // 🎯 CORRECTION: Libération automatique quand le composant se ferme
  useEffect(() => {
    return () => {
      // Libérer automatiquement si cette section avait le contrôle
      if (globalActiveSection === sectionId) {
        console.log('📸 [PhotoCapture] Libération automatique au démontage:', sectionId);
        globalActiveSection = null;
      }
    };
  }, [sectionId]);

  // 🎯 AMÉLIORATION: Exposer les méthodes pour le parent
  useImperativeHandle(ref, () => ({
    stopCamera: () => {
      console.log('📸 [PhotoCapture] stopCamera appelé via ref');
      stopCamera();
    },
    isShowingCamera: () => showCamera,
    // 🎯 CORRECTION: Méthode pour libérer cette section et débloquer les autres
    releaseCamera: () => {
      console.log('📸 [PhotoCapture] releaseCamera appelé - libération forcée pour:', sectionId);
      if (globalActiveSection === sectionId) {
        globalActiveSection = null;
        console.log('📸 [PhotoCapture] Section libérée:', sectionId);
      }
      stopCamera();
    }
  }));

  /**
   * Arrêter toutes les caméras (version brutale)
   */
  const stopAllCameras = async () => {
    console.log('📸 [PhotoCapture] stopAllCameras appelé');
    // Stopper tous les éléments vidéo de la page
    const allVideos = document.querySelectorAll('video');
    console.log('📸 [PhotoCapture] Vidéos trouvées:', allVideos.length);
    allVideos.forEach((video, index) => {
      console.log(`📸 [PhotoCapture] Traitement vidéo ${index + 1}:`, video);
      if (video.srcObject) {
        const stream = video.srcObject;
        console.log(`📸 [PhotoCapture] Stream trouvé pour vidéo ${index + 1}:`, stream);
        if (stream.getTracks) {
          const tracks = stream.getTracks();
          console.log(`📸 [PhotoCapture] Tracks trouvés pour vidéo ${index + 1}:`, tracks.length);
          tracks.forEach(track => {
            console.log(`📸 [PhotoCapture] Arrêt track:`, track.label);
            track.stop();
          });
        }
        video.srcObject = null;
      }
      video.pause();
    });
  };

  /**
   * Démarrer la webcam (version corrigée)
   */
  const startCamera = async () => {
    // Protection contre les clics multiples
    if (isStartingCamera || cameraLoading) {
      console.log('📸 [PhotoCapture] Démarrage déjà en cours, ignoré');
      return;
    }
    
    try {
      console.log('📸 [PhotoCapture] startCamera appelé');
      
      // 🎯 CORRECTION: TOUJOURS prendre le contrôle - pas de blocage !
      console.log('📸 [PhotoCapture] Prise de contrôle forcée par:', sectionId);
      if (globalActiveSection && globalActiveSection !== sectionId) {
        console.log('📸 [PhotoCapture] Reprise de contrôle depuis:', globalActiveSection);
      }
      globalActiveSection = sectionId;
      
      setIsStartingCamera(true);
      setErrors([]);
      setCameraLoading(true);
      setVideoReady(false);
      setIsBlocked(false);
      
      console.log('📸 [PhotoCapture] Démarrage caméra simplifié...');
      
      // Arrêt brutal et simple
      console.log('📸 [PhotoCapture] Arrêt de toutes les caméras...');
      await stopAllCameras();

      let preferredCameraId = selectedCameraId;
      if (!preferredCameraId) {
        const videoDevices = await enumerateCameras();
        const preferredCamera = pickPreferredCamera(videoDevices);
        preferredCameraId = preferredCamera?.deviceId || '';

        if (preferredCameraId) {
          setSelectedCameraId(preferredCameraId);
        }
      }

      const primaryConstraints = buildVideoConstraints(preferredCameraId);
      
      // Demander en priorité la caméra arrière, avec repli sur le mode environment
      console.log('📸 [PhotoCapture] Demande getUserMedia avec contraintes:', primaryConstraints);

      let stream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({ 
          video: primaryConstraints,
          audio: false
        });
      } catch (primaryError) {
        console.warn('📸 [PhotoCapture] Échec de la caméra préférée, repli sur environment:', primaryError);
        stream = await navigator.mediaDevices.getUserMedia({ 
          video: buildVideoConstraints(''),
          audio: false
        });
      }
      
      console.log('📸 [PhotoCapture] Stream obtenu:', stream);
      setCameraStream(stream);
      setShowCamera(true);

      void enumerateCameras();
      
      // Attendre que le composant soit rendu avec la vidéo
      await new Promise(resolve => setTimeout(resolve, 100));
      
      console.log('📸 [PhotoCapture] videoRef.current existe?', !!videoRef.current);
      
      if (videoRef.current) {
        console.log('📸 [PhotoCapture] Assignation du stream à la vidéo...');
        const video = videoRef.current;
        console.log('📸 [PhotoCapture] Element video:', video);
        video.srcObject = stream;
        console.log('📸 [PhotoCapture] srcObject assigné');
        
        // Forcer le démarrage immédiat (muted déjà dans JSX)
        
        // Promesse pour attendre le vrai démarrage
        try {
          console.log('📸 [PhotoCapture] Tentative play()...');
          await video.play();
          console.log('📸 [PhotoCapture] Play() réussi');
          
          // Attendre que la vidéo soit vraiment prête
          if (video.readyState >= 2) {
            console.log('📸 [PhotoCapture] Vidéo prête immédiatement');
            setCameraLoading(false);
            setVideoReady(true);
            setIsStartingCamera(false);
          } else {
            console.log('📸 [PhotoCapture] Attente du chargement vidéo...');
            video.oncanplay = () => {
              console.log('📸 [PhotoCapture] oncanplay déclenché');
              setCameraLoading(false);
              setVideoReady(true);
              setIsStartingCamera(false);
            };
            
            // Timeout de sécurité
            setTimeout(() => {
              console.log('📸 [PhotoCapture] Timeout - force ready');
              setCameraLoading(false);
              setVideoReady(true);
              setIsStartingCamera(false);
            }, 2000);
          }
        } catch (playError) {
          console.error('📸 [PhotoCapture] Erreur play():', playError);
          // Essayer quand même de marquer comme prêt
          setCameraLoading(false);
          setVideoReady(true);
          setIsStartingCamera(false);
        }
      } else {
        console.error('📸 [PhotoCapture] videoRef.current est null après 100ms');
        // Essayer encore une fois avec un délai plus long
        setTimeout(async () => {
          console.log('📸 [PhotoCapture] Nouvelle tentative videoRef après 500ms...');
          if (videoRef.current) {
            const video = videoRef.current;
            video.srcObject = stream;
            try {
              await video.play();
              setCameraLoading(false);
              setVideoReady(true);
              setIsStartingCamera(false);
            } catch (e) {
              console.error('📸 [PhotoCapture] Erreur play tardif:', e);
              setCameraLoading(false);
              setVideoReady(true);
              setIsStartingCamera(false);
            }
          } else {
            console.error('📸 [PhotoCapture] videoRef.current toujours null après 500ms');
            setCameraLoading(false);
            setErrors([t('errors.camera.elementNotFound')]);
            setIsStartingCamera(false);
          }
        }, 500);
      }
      
    } catch (error) {
      console.error('📸 [PhotoCapture] Erreur caméra:', error);
      setCameraLoading(false);
      setIsStartingCamera(false);
      setErrors([t('errors.camera.generic', { error: error.message })]);
    } finally {
      setIsStartingCamera(false);
    }
  };

  /**
   * Arrêter la webcam
   */
  const stopCamera = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => track.stop());
      setCameraStream(null);
    }
    
    // 🎯 COORDINATION: Libérer le contrôle de la caméra
    if (globalActiveSection === sectionId) {
      globalActiveSection = null;
      console.log('📸 [PhotoCapture] Section libérée:', sectionId);
    }
    
    // Nettoyer les gestionnaires d'événements
    if (videoRef.current) {
      const video = videoRef.current;
      video.onloadedmetadata = null;
      video.oncanplay = null;
      video.onplaying = null;
      video.onloadeddata = null;
      video.onerror = null;
      video.srcObject = null;
    }
    
    setShowCamera(false);
    setCameraLoading(false);
    setVideoReady(false);
    setIsBlocked(false);
  };

  /**
   * Capturer une photo depuis la webcam (version améliorée)
   */
  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) {
      setErrors(['Erreur: Vidéo non initialisée']);
      return;
    }

    const video = videoRef.current;
    const canvas = canvasRef.current;
    
    // Debug info
    console.log('=== CAPTURE DEBUG ===');
    console.log('Video readyState:', video.readyState);
    console.log('Video dimensions:', video.videoWidth, 'x', video.videoHeight);
    console.log('Video paused:', video.paused);
    console.log('Video current time:', video.currentTime);
    
    // Vérifier que la vidéo a au moins les données courantes (readyState >= 2)
    if (video.readyState < 2) {
      setErrors([`Erreur: Vidéo non prête (état: ${video.readyState}). Attendez le chargement...`]);
      return;
    }
    
    if (video.videoWidth === 0 || video.videoHeight === 0) {
      setErrors(['Erreur: Aucune image vidéo disponible. Dimensions: ' + video.videoWidth + 'x' + video.videoHeight]);
      return;
    }

    try {
      const context = canvas.getContext('2d');

      // Définir la taille du canvas
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      // Dessiner l'image vidéo sur le canvas
      context.drawImage(video, 0, 0);

      // Vérifier que quelque chose a été dessiné
      const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
      const hasData = imageData.data.some(pixel => pixel !== 0);
      
      if (!hasData) {
        setErrors(['Erreur: Image capturée vide']);
        return;
      }

      console.log('Image capturée avec succès:', canvas.width + 'x' + canvas.height);

      // 🎯 AMÉLIORATION: Effet flash visuel
      setCaptureFlash(true);
      setTimeout(() => setCaptureFlash(false), 200);

      // Convertir en blob
      canvas.toBlob((blob) => {
        if (blob) {
          const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
          const photoFile = new File([blob], `capture_${timestamp}.jpg`, {
            type: 'image/jpeg'
          });

          addPhoto(photoFile, true);
          console.log('Photo capturée avec succès:', photoFile);
          
          // 🎯 AMÉLIORATION: Ne plus fermer la caméra après capture
          // stopCamera(); // <- Supprimé pour garder la caméra active
          console.log('📸 Caméra conservée active pour captures supplémentaires');
        } else {
          setErrors(['Erreur lors de la création de l\'image']);
        }
      }, 'image/jpeg', 0.9);

    } catch (error) {
      console.error('Erreur lors de la capture:', error);
      setErrors(['Erreur lors de la capture: ' + error.message]);
    }
  };

  /**
   * Gérer l'upload de fichiers
   */
  const handleFileUpload = (event) => {
    const files = Array.from(event.target.files);
    
    files.forEach(file => {
      if (validateFile(file)) {
        addPhoto(file, false);
      }
    });

    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };


  /**
   * Valider un fichier
   */
  const validateFile = (file) => {
    const newErrors = [];

    // Vérifier le format
    const extension = file.name.split('.').pop().toLowerCase();
    if (!allowedFormats.includes(extension)) {
      newErrors.push(t('errors.file.unsupportedFormat', { extension, formats: allowedFormats.join(', ') }));
    }

    // Vérifier la taille
    if (file.size > maxFileSize) {
      newErrors.push(t('errors.file.tooLarge', { size: (file.size / 1024 / 1024).toFixed(1) }));
    }

    if (newErrors.length > 0) {
      setErrors(prev => [...prev, ...newErrors]);
      return false;
    }

    return true;
  };

  /**
   * Ajouter une photo
   */
  const addPhoto = (file, isFromCamera) => {
    if (photos.length >= maxPhotos) {
      setErrors(prev => [...prev, t('errors.maxPhotos', { max: maxPhotos })]);
      return;
    }

    const photoUrl = URL.createObjectURL(file);
    const newPhoto = {
      id: `temp_${Date.now()}_${Math.random()}`,
      file: file,
      photo_url: photoUrl,
      photo_type: photoType,
      description: '',
      isTemporary: true,
      isFromCamera,
      order_index: photos.length
    };

    onPhotosChange([...photos, newPhoto]);
  };

  /**
   * Supprimer une photo
   */
  const removePhoto = (photoId) => {
    const photoToRemove = photos.find(p => p.id === photoId);
    if (photoToRemove && photoToRemove.photo_url.startsWith('blob:')) {
      URL.revokeObjectURL(photoToRemove.photo_url);
    }

    const newPhotos = photos.filter(p => p.id !== photoId);
    onPhotosChange(newPhotos);
  };

  /**
   * Mettre à jour la description d'une photo
   */
  const updatePhotoDescription = (photoId, description) => {
    const newPhotos = photos.map(photo =>
      photo.id === photoId ? { ...photo, description } : photo
    );
    onPhotosChange(newPhotos);
  };

  /**
   * Effacer les erreurs
   */
  const clearErrors = () => {
    setErrors([]);
  };

  return (
    <Paper 
      elevation={1} 
      sx={{ 
        p: 2, 
        mb: 2,
        // 🎯 COORDINATION: Style désactivé si bloqué par autre section
        opacity: isBlocked ? 0.5 : 1,
        pointerEvents: isBlocked ? 'none' : 'auto',
        position: 'relative'
      }}
    >
      {/* 🎯 COORDINATION: Overlay si section bloquée */}
      {isBlocked && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10
          }}
        >
          <Alert severity="info" sx={{ m: 2 }}>
            <Typography variant="body2">
              {t('ui.cameraInUse', 'Caméra utilisée par une autre section')}
            </Typography>
          </Alert>
        </Box>
      )}
      
      {/* En-tête section */}
      <Box 
        display="flex" 
        alignItems="center" 
        justifyContent="space-between"
        onClick={() => setExpanded(!expanded)}
        sx={{ cursor: 'pointer', mb: expanded ? 2 : 0 }}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <PhotoCamera color="primary" />
          <Typography variant="h6">
            {displayTitle}
          </Typography>
          {optional && (
            <Chip 
              label={t('ui.optional')} 
              size="small" 
              color="info" 
              variant="outlined"
            />
          )}
          {photos.length > 0 && (
            <Badge badgeContent={photos.length} color="primary">
              <Chip label={t('ui.photos')} size="small" />
            </Badge>
          )}
        </Box>
        <IconButton>
          {expanded ? <ExpandLess /> : <ExpandMore />}
        </IconButton>
      </Box>

      {/* Contenu collapsible */}
      <Collapse in={expanded}>
        {/* Erreurs */}
        {errors.length > 0 && (
          <Alert 
            severity="error" 
            onClose={clearErrors}
            sx={{ mb: 2 }}
          >
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              {errors.map((error, index) => (
                <li key={index}>{error}</li>
              ))}
            </ul>
          </Alert>
        )}

        {/* Actions de capture - Désormais vide ou seulement pour les erreurs */}
        {!readOnly && photos.length < maxPhotos && showCamera && cameraLoading && (
          <Box display="flex" justifyContent="center" mb={2}>
            <Button
              variant="contained"
              startIcon={<CircularProgress size={16} color="inherit" />}
              disabled
              size="small"
            >
              {t('ui.loadingCamera')}
            </Button>
          </Box>
        )}

        {/* Webcam */}
        {showCamera && (
          <Box mb={2} sx={{ textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary" mb={1}>
              {cameraLoading ? t('ui.cameraLoading') : t('ui.cameraReady')}
            </Typography>
            
            {/* Statut de debug - seulement si vidéo prête */}
            {!cameraLoading && videoRef.current && videoRef.current.readyState >= 2 && videoRef.current.videoWidth > 0 && (
              <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                État vidéo: {videoRef.current.readyState}/4 | 
                Dimensions: {videoRef.current.videoWidth}x{videoRef.current.videoHeight} |
                {videoRef.current.paused ? ' ⏸️ Pause' : ' ▶️ Lecture'}
              </Typography>
            )}
            
            <Box 
              sx={{ 
                position: 'relative',
                display: 'inline-block',
                borderRadius: '8px',
                overflow: 'hidden',
                border: cameraLoading ? '2px dashed #ccc' : 
                       (videoRef.current?.readyState >= 2 ? '2px solid #4caf50' : '2px solid #ff9800'),
                backgroundColor: cameraLoading ? '#f5f5f5' : 'transparent',
                mb: 2,
                // 🎯 AMÉLIORATION: Effet flash lors de la capture
                transition: 'all 0.2s ease',
                boxShadow: captureFlash ? '0 0 30px rgba(76, 175, 80, 0.8)' : 'none',
                transform: captureFlash ? 'scale(1.02)' : 'scale(1)'
              }}
            >
              {cameraLoading && (
                <Box 
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'rgba(255, 255, 255, 0.8)',
                    zIndex: 1
                  }}
                >
                  <CircularProgress />
                </Box>
              )}
              
              {/* Effet flash overlay */}
              {captureFlash && (
                <FlashOverlay
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    zIndex: 2,
                    animation: 'flash 0.2s ease-out'
                  }}
                />
              )}
              
              <video
                ref={videoRef}
                playsInline
                muted
                style={{
                  width: '100%',
                  maxWidth: '400px',
                  height: 'auto',
                  display: 'block'
                }}
              />
            </Box>
            
            <canvas ref={canvasRef} style={{ display: 'none' }} />
          </Box>
        )}

        {/* 🎯 NOUVELLE SECTION: Actions de capture sous la caméra */}
        {!readOnly && (
          <Box display="flex" gap={2} mb={2} flexWrap="wrap" justifyContent="center" alignItems="center">
            {!showCamera && !cameraLoading && photos.length < maxPhotos ? (
              <Button
                variant="contained"
                startIcon={<PhotoCamera />}
                onClick={startCamera}
                size="small"
              >
                {t('ui.buttons.takePhoto')}
              </Button>
            ) : showCamera && !cameraLoading && videoReady && photos.length < maxPhotos ? (
              <Button
                variant="contained"
                color="success"
                onClick={capturePhoto}
                startIcon={<PhotoCamera />}
                size="small"
              >
                {t('ui.buttons.capture')}
              </Button>
            ) : photos.length >= maxPhotos ? (
              <Button
                variant="outlined"
                disabled
                startIcon={<PhotoCamera />}
                size="small"
              >
                {t('ui.buttons.limitReached')}
              </Button>
            ) : null}

            {/* Upload toujours disponible */}
            <Button
              variant="outlined"
              startIcon={<Upload />}
              component="label"
              size="small"
            >
              {t('ui.buttons.upload')}
              <VisuallyHiddenInput
                ref={fileInputRef}
                type="file"
                multiple
                accept={allowedFormats.map(f => `.${f}`).join(',')}
                onChange={handleFileUpload}
              />
            </Button>

            {cameraLoading && showCamera && (
              <Button
                variant="outlined"
                color="warning"
                onClick={() => {
                  setCameraLoading(false);
                  console.log('Forçage du démarrage caméra');
                }}
                size="small"
              >
                {t('ui.buttons.forceStart')}
              </Button>
            )}

            {isEnumeratingCameras && (
              <Typography variant="caption" color="text.secondary" alignSelf="center">
                {t('ui.detectingCameras', 'Détection des caméras...')}
              </Typography>
            )}

            {availableCameras.length > 1 && !isEnumeratingCameras && (
              <Chip
                label={t('ui.backCameraPreferred', 'Caméra arrière privilégiée')}
                size="small"
                color="success"
                variant="outlined"
              />
            )}

            {/* Compteur de photos - toujours visible */}
            <Typography variant="body2" color="text.secondary" alignSelf="center">
              {t('ui.photoCount', { current: photos.length, max: maxPhotos })}
            </Typography>
          </Box>
        )}

        {/* Grille de photos */}
        {photos.length > 0 && (
          <Grid container spacing={2}>
            {photos.map((photo, index) => (
              <Grid item xs={6} sm={4} md={3} key={photo.id}>
                <PhotoPreview elevation={2}>
                  <PhotoImage
                    src={photo.photo_url}
                    alt={`Photo ${index + 1}`}
                    loading="lazy"
                  />
                  
                  {!readOnly && (
                    <PhotoActions>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedPhoto(photo);
                        }}
                      >
                        <Visibility fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          removePhoto(photo.id);
                        }}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </PhotoActions>
                  )}

                  {photo.isFromCamera && (
                    <Chip
                      label={t('ui.webcam')}
                      size="small"
                      color="info"
                      sx={{
                        position: 'absolute',
                        bottom: 4,
                        left: 4
                      }}
                    />
                  )}
                </PhotoPreview>

                {/* Description de la photo */}
                {!readOnly && (
                  <TextField
                    fullWidth
                    size="small"
                    placeholder={t('ui.descriptionPlaceholder')}
                    value={photo.description || ''}
                    onChange={(e) => updatePhotoDescription(photo.id, e.target.value)}
                    sx={{ mt: 1 }}
                    inputProps={{ maxLength: 500 }}
                  />
                )}
              </Grid>
            ))}
          </Grid>
        )}

        {/* Message si aucune photo */}
        {photos.length === 0 && expanded && (
          <Box textAlign="center" py={3}>
            <PhotoCamera sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
            <Typography color="text.secondary">
              {optional ? t('ui.noPhotosOptional') : t('ui.addPhotos')}
            </Typography>
          </Box>
        )}
      </Collapse>
    </Paper>
  );
});

export default PhotoCaptureSection;
